/**
 * <hdt-decimal-range>
 * Renders a dual-handle range slider over a hidden list of checkboxes.
 * Used to filter list-type metafield filters whose values are decimals.
 *
 * Markup contract (rendered by the Liquid `list` case):
 *   <hdt-decimal-range data-min="..." data-max="..." data-step="0.01">
 *     <input type="range" data-range-handle="min" ...>
 *     <input type="range" data-range-handle="max" ...>
 *     <input type="number" data-range-input="min" ...>
 *     <input type="number" data-range-input="max" ...>
 *     <div class="hdt-decimal-range__values" hidden>
 *       <input type="checkbox" name="filter.p.m...." value="..." data-decimal-value="1.25">
 *       ...
 *     </div>
 *   </hdt-decimal-range>
 */
if (!customElements.get('hdt-decimal-range')) {
  class HdtDecimalRange extends HTMLElement {
    constructor() {
      super();
      this._refetchTimer = null;
      this._onHandle = this._onHandle.bind(this);
      this._onInput  = this._onInput.bind(this);
    }

    connectedCallback() {
      this.min  = parseFloat(this.dataset.min);
      this.max  = parseFloat(this.dataset.max);
      this.step = parseFloat(this.dataset.step) || 0.01;

      this.handleMin = this.querySelector('[data-range-handle="min"]');
      this.handleMax = this.querySelector('[data-range-handle="max"]');
      this.inputMin  = this.querySelector('[data-range-input="min"]');
      this.inputMax  = this.querySelector('[data-range-input="max"]');
      this.boxes     = this.querySelectorAll('.hdt-decimal-range__values input[type="checkbox"]');

      if (!this.handleMin || !this.handleMax) return;

      // Live preview while dragging — updates UI only, no network.
      this.handleMin.addEventListener('input', this._onHandle);
      this.handleMax.addEventListener('input', this._onHandle);

      // Commit on release / blur — toggles checkboxes & fires `change` so
      // the parent facets form re-fetches via the Section Rendering API.
      this.handleMin.addEventListener('change', this._onInput);
      this.handleMax.addEventListener('change', this._onInput);
      this.inputMin.addEventListener('change',  this._onInput);
      this.inputMax.addEventListener('change',  this._onInput);
    }

    disconnectedCallback() {
      if (this._refetchTimer) clearTimeout(this._refetchTimer);
    }

    /** Visual-only sync during drag */
    _onHandle() {
      let lo = parseFloat(this.handleMin.value);
      let hi = parseFloat(this.handleMax.value);
      if (lo > hi) [lo, hi] = [hi, lo];

      this.inputMin.value = lo;
      this.inputMax.value = hi;
      this._updateProgress(lo, hi);
    }

    /** Commits the range: syncs all four inputs, toggles checkboxes, triggers fetch */
    _onInput(e) {
      const fromNumberInput = e && e.target && e.target.matches('[data-range-input]');

      let lo = parseFloat(fromNumberInput ? this.inputMin.value  : this.handleMin.value);
      let hi = parseFloat(fromNumberInput ? this.inputMax.value  : this.handleMax.value);

      if (Number.isNaN(lo)) lo = this.min;
      if (Number.isNaN(hi)) hi = this.max;
      lo = Math.max(this.min, Math.min(this.max, lo));
      hi = Math.max(this.min, Math.min(this.max, hi));
      if (lo > hi) [lo, hi] = [hi, lo];

      this.handleMin.value = lo;
      this.handleMax.value = hi;
      this.inputMin.value  = lo;
      this.inputMax.value  = hi;
      this._updateProgress(lo, hi);

      // Toggle the real form inputs. Fire `change` only when state actually flips.
      let anyChanged = false;
      this.boxes.forEach(cb => {
        const v = parseFloat(cb.dataset.decimalValue);
        const want = v >= lo && v <= hi;
        if (cb.checked !== want) {
          cb.checked = want;
          anyChanged = true;
        }
      });

      if (anyChanged) this._scheduleRefetch();
    }

    _updateProgress(lo, hi) {
      const total = (this.max - this.min) || 1;
      const minPct = ((lo - this.min) / total) * 100;
      const maxPct = 100 - ((hi - this.min) / total) * 100;
      this.style.setProperty('--min-progress', `${minPct}%`);
      this.style.setProperty('--max-progress', `${maxPct}%`);
    }

    /**
     * Dispatch a single `change` on the parent <form> after a short debounce,
     * so the theme's facets controller refetches once even if many checkboxes
     * just flipped at once.
     */
    _scheduleRefetch() {
      if (this._refetchTimer) clearTimeout(this._refetchTimer);
      this._refetchTimer = setTimeout(() => {
        const form = this.closest('form');
        if (form) {
          form.dispatchEvent(new Event('input',  { bubbles: true }));
          form.dispatchEvent(new Event('change', { bubbles: true }));
        }
      }, 250);
    }
  }

  customElements.define('hdt-decimal-range', HdtDecimalRange);
}